import type { ReactNode } from 'react';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatSlotDuration } from '@jackedlogs/shared';
import {
  IconButton,
  METER_ROW_COLUMN,
  TimerControlsRow,
  type TimerControl,
} from '../index';

import type { useTheme } from './theme';
import { HOLD_STEP_SEC, type TimerStartHandlers } from './timerStartHandlers';

// The AXIS row of a duration set (MX-5 slice 3) — the row above the effort
// band, in its two states. It is the twin of <EffortTimerSlot>, which owns the
// column below it, and it exists for the same reason: FocusPanelEffort stays
// the coordinator (reps vs duration, the nudge, the CTA, the fail tip) while
// each row's CONTENT lives in a file of its own.
//
//   idle     → the word "Effort" on the left, the gear over the column.
//   running  → the timer's commands on the left, the clock over the column.
//
// The clock sits in the axis' trailing slot, pinned to the SAME width as the
// meter's action column below it (METER_ROW_COLUMN), so it ends on the row's
// right edge instead of floating near it. That alignment is the whole reason
// MeterRow was extracted in slice 1 — here is the second thing on that grid.
// (While the timer runs the row below has no column at all, so the clock lands
// over the end of the band. Still the same right edge.)
//
// The commands are <TimerControlsRow>, the same fan the rest banner uses: one
// place owns the touch expansion and the extra gap before the destructive ✕
// (inbox 9b5daf1a). What changes here is only the TONE — `default`, because
// this row is flat, not a brand-filled banner.

/** Below this screen width the direction flip drops off the row. 5 × 36 + 76
 *  = 288 needs 328pt of usable width; at ~310dp there is no longer room for
 *  the fifth box, and ⇄ is the one command with another way in (cancel and
 *  restart, or the gear's default). Losing a button beats an overflowing row
 *  or five targets squeezed under the touch floor (a11y floor, MX-5). */
const FLIP_DROPS_BELOW_DP = 310;

/** The axis' LEFT slot: the word, or the running timer's commands. */
export function EffortDurationAxisLeading({
  timer,
  word,
  theme,
}: {
  timer?: TimerStartHandlers;
  /** The tappable "Effort" word — built by FocusPanelEffort, because the reps
   *  variant renders the very same node in its own axis slot. One control, one
   *  definition; this file only decides WHERE it goes. */
  word: ReactNode;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const styles = makeStyles(theme);
  const running = timer?.running;
  if (!running) return <>{word}</>;

  const controls: TimerControl[] = [];
  // ±10 only on a countdown: there is no target to move on a stopwatch, and a
  // disabled pair of buttons would take the room the others need.
  if (running.isDown) {
    controls.push(
      {
        key: 'sub',
        label: `−${HOLD_STEP_SEC}`,
        a11yLabel: t('mobile.logger.holdSubTen', { defaultValue: '-10s' }),
        onPress: running.onSubTen,
      },
      {
        key: 'add',
        label: `+${HOLD_STEP_SEC}`,
        a11yLabel: t('mobile.logger.holdAddTen', { defaultValue: '+10s' }),
        onPress: running.onAddTen,
      },
    );
  }
  if (width >= FLIP_DROPS_BELOW_DP) {
    controls.push({
      key: 'flip',
      icon: 'arrow-left-right',
      a11yLabel: t('mobile.logger.holdFlip', {
        defaultValue: 'Switch direction',
      }),
      onPress: running.onFlip,
    });
  }
  controls.push(
    {
      key: 'pause',
      icon: running.isPaused ? 'play' : 'pause',
      a11yLabel: running.isPaused
        ? t('mobile.logger.restResume', { defaultValue: 'Resume' })
        : t('mobile.logger.restPause', { defaultValue: 'Pause' }),
      onPress: running.onPauseToggle,
    },
    {
      key: 'cancel',
      icon: 'close',
      a11yLabel: t('mobile.logger.holdCancel', { defaultValue: 'Cancel' }),
      // Spoken, because cancelling a running hold throws the seconds away and
      // the ✕ glyph alone doesn't say what it discards.
      onPress: running.onCancel,
      variant: 'cancel',
    },
  );
  // There is NO ✓ here on purpose (D7): the band below is the confirmation, so
  // a check would be a second road to the same act — the one the design removed
  // to get from three taps to two.
  return (
    <TimerControlsRow
      theme={theme}
      controls={controls}
      defaultVariant="default"
      // Five controls in the width the banner used for four, so the touch zones
      // are squared up rather than left to overlap: gap 8 below, horizontal
      // slop 4 here ⇒ 44×52 each, edges meeting exactly. 5×36 + 4×8 = 212, and
      // 212 + 8 + 48 (clock, METER_ROW_COLUMN) = 268 of the 328 usable at
      // 360dp. ⚠️ The clock GROWS rather than truncating (see clockWrap), so
      // the margin left over is what a wide readout eats before these controls
      // start wrapping to a second line — and overtime's + costs a character.
      hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
      style={styles.controls}
    />
  );
}

/** The axis' RIGHT slot, over the meter's action column: the gear, or the
 *  running clock. */
export function EffortDurationAxisTrailing({
  timer,
  onOpenEffortScale,
  theme,
}: {
  timer?: TimerStartHandlers;
  /** Fallback for the gear when this entry has no timer to configure. */
  onOpenEffortScale: () => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // Same width as the meter's action column, so whatever sits here ends on the
  // same right edge on every screen.
  const slotWidth = METER_ROW_COLUMN;
  const running = timer?.running;

  if (running) {
    const time = formatSlotDuration(running.displaySec);
    // Past the target the clock shows the overshoot, so the sign is what tells
    // the two apart: "+0:12" is twelve seconds MORE than asked, "0:12" is
    // twelve left. A glyph, not a tint — the state has to survive a
    // colour-blind read and a washed-out screen in a gym (DESIGN.md).
    const clockText = running.isOvertime ? `+${time}` : time;
    // The three time states (overtime / remaining / elapsed). Pause is
    // ORTHOGONAL — the clock can be held in any of them — so it wraps the base
    // reading rather than adding a fourth branch. One key with a {{label}}
    // placeholder, never two sentences concatenated by hand (the AN-2 lesson).
    const timeA11y = running.isOvertime
      ? t('mobile.logger.holdClockOvertimeA11y', {
          time,
          defaultValue: '{{time}} past the target',
        })
      : running.isDown
        ? t('mobile.logger.holdClockRemainingA11y', {
            time,
            defaultValue: '{{time}} remaining',
          })
        : t('mobile.logger.holdClockElapsedA11y', {
            time,
            defaultValue: '{{time}} elapsed',
          });
    const a11yLabel = running.isPaused
      ? t('mobile.logger.holdClockPausedA11y', {
          label: timeA11y,
          defaultValue: '{{label}}, paused',
        })
      : timeA11y;
    return (
      <View style={[styles.clockWrap, { minWidth: slotWidth }]}>
        <Text
          style={[styles.clock, running.isPaused && styles.clockPaused]}
          numberOfLines={1}
          accessible
          // Deliberately NOT a live region: this text changes every second, and
          // announcing it every second would talk over everything else the
          // athlete is doing. It reads on demand, like a clock on the wall.
          accessibilityLabel={a11yLabel}
        >
          {clockText}
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.gearWrap, { width: slotWidth }]}>
      <IconButton
        theme={theme}
        name="settings"
        onPress={timer?.onOpenSettings ?? onOpenEffortScale}
        // 2 towards the word at its left; 8 above and to the right, both free.
        // BELOW there are only the axis row's 4pt of margin before the action
        // slot, so the bottom slop is capped at 4 — it still clears the floor
        // (36 + 8 + 4 = 48) without reaching into the row under it (a11y audit
        // 2026-07-22).
        hitSlop={{ top: 8, bottom: 4, left: 2, right: 8 }}
        accessibilityLabel={t('mobile.logger.timerSettings', {
          defaultValue: 'Timer defaults for this exercise',
        })}
      />
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    // Takes the free width and packs the commands to the LEFT — the clock owns
    // the right edge, and a row that justified to the end would slide the
    // buttons under it as soon as one dropped out.
    // gap s3, not the row's default s2: it is what lets the touch zones meet
    // without overlapping (see the hitSlop note at the callsite).
    controls: { flex: 1, justifyContent: 'flex-start', gap: theme.spacing.s3 },
    gearWrap: { alignItems: 'flex-end' },
    // minWidth, not width: it lines up with the column below when there is
    // room, and grows instead of TRUNCATING when there isn't. A fixed 48 with
    // an lg font printed `0:…` for `0:31` — a clock that can't say the time is
    // worse than a clock that borrows a few points from the commands beside it.
    clockWrap: { alignItems: 'flex-end', justifyContent: 'center', flexShrink: 0 },
    // md + mono + tabular: big enough to read at a glance, small enough that
    // `1h02` fits the column's width. Tabular digits keep it from twitching
    // every second. Same family as the meter's segment numbers and the slot's
    // result, so the three readouts of one set look like one instrument.
    clock: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.md,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
      textAlign: 'right',
    },
    // Paused: a quieter INK, not an opacity — the same fix as the rest row's
    // countdown, applied to its twin so the two can't diverge. Dimming `ink`
    // with alpha is a blend and eats the contrast (0.55 = 4.18:1 on the light
    // families, under the floor); `ink2` is checked by
    // pausedReadoutContrast.test.ts. The number goes quiet, it is italic, and
    // the command beside it has turned into ▶ — three cues, no colour alone.
    clockPaused: { color: p.ink2, fontStyle: 'italic' },
  });
}
